import { getSecureFileUrl as getSecureFileUrlApi } from '@/assets/js/api/manage'

/**
 * 文件防盗链工具类
 */
class FileUtils {
  constructor() {
    // 缓存已获取的带token URL，避免重复请求
    this.urlCache = new Map()
    // 缓存过期时间（毫秒），默认30分钟
    this.cacheExpireTime = 30 * 60 * 1000
  }

  /**
   * 获取带防盗链的文件URL
   * @param {string} filePath 文件路径
   * @param {boolean} useCache 是否使用缓存，默认true
   * @returns {Promise<string>} 返回带token的文件URL
   */
  async getSecureUrl(filePath, useCache = true) {
    if (!filePath) {
      return Promise.resolve('')
    }

    // 如果是 base64 图片数据，直接返回
    if (filePath.startsWith('data:image/') || filePath.startsWith('data:application/')) {
      return Promise.resolve(filePath)
    }

    // 如果是完整的HTTP URL，需要判断是否是FastDFS URL
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      // 如果URL中包含 /group（FastDFS路径特征），需要调用防盗链接口
      if (filePath.includes('/group')) {
        // 提取FastDFS路径部分（group1/M00/...）
        const match = filePath.match(/(group\d+\/M\d+\/.+?)(\?|$)/)
        if (match && match[1]) {
          const fastdfsPath = match[1]
          // 使用FastDFS路径调用防盗链接口
          return this.getSecureUrlFromApi(fastdfsPath, useCache)
        }
      }
      // 其他HTTP URL直接返回
      return Promise.resolve(filePath)
    }

    // FastDFS相对路径，调用防盗链接口
    return this.getSecureUrlFromApi(filePath, useCache)
  }

  /**
   * 从API获取带防盗链的URL
   * @param {string} filePath FastDFS文件路径
   * @param {boolean} useCache 是否使用缓存
   * @returns {Promise<string>} 返回带token的文件URL
   */
  async getSecureUrlFromApi(filePath, useCache = true) {

    // 检查缓存
    if (useCache && this.urlCache.has(filePath)) {
      const cached = this.urlCache.get(filePath)
      const now = Date.now()
      
      // 检查缓存是否过期
      if (now - cached.timestamp < this.cacheExpireTime) {
        return Promise.resolve(cached.url)
      } else {
        // 缓存过期，删除
        this.urlCache.delete(filePath)
      }
    }

    try {
      // 调用后端接口获取带token的URL
      const response = await getSecureFileUrlApi(filePath)
      
      if (response.success && response.result) {
        const secureUrl = response.result
        
        // 缓存结果
        if (useCache) {
          this.urlCache.set(filePath, {
            url: secureUrl,
            timestamp: Date.now()
          })
        }
        
        return secureUrl
      } else {
        // 如果接口返回失败，回退到原来的方式
        console.warn('获取防盗链URL失败，回退到原始路径:', response.message)
        return this.getFallbackUrl(filePath)
      }
    } catch (error) {
      // 如果接口调用失败，回退到原来的方式
      console.error('调用防盗链接口失败:', error)
      return this.getFallbackUrl(filePath)
    }
  }

  /**
   * 获取回退URL（当防盗链接口失败时使用）
   * @param {string} filePath 文件路径
   * @returns {string} 回退URL
   */
  getFallbackUrl(filePath) {
    if (!filePath) return ''
    
    // 如果已经是完整URL，直接返回
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath
    }
    
    // 使用配置的静态域名
    const staticDomain = window._CONFIG && window._CONFIG['staticDomainURL']
    if (staticDomain) {
      return `${staticDomain}/${filePath}`
    }
    
    // 如果没有配置静态域名，返回相对路径
    return filePath
  }

  /**
   * 批量获取带防盗链的文件URL
   * @param {Array<string>} filePaths 文件路径数组
   * @param {boolean} useCache 是否使用缓存，默认true
   * @returns {Promise<Array<string>>} 返回带token的文件URL数组
   */
  async getSecureUrls(filePaths, useCache = true) {
    if (!Array.isArray(filePaths)) {
      return Promise.resolve([])
    }

    const promises = filePaths.map(filePath => this.getSecureUrl(filePath, useCache))
    return Promise.all(promises)
  }

  /**
   * 清除URL缓存
   * @param {string} filePath 可选，指定要清除的文件路径，不传则清除所有缓存
   */
  clearCache(filePath) {
    if (filePath) {
      this.urlCache.delete(filePath)
    } else {
      this.urlCache.clear()
    }
  }

  /**
   * 设置缓存过期时间
   * @param {number} expireTime 过期时间（毫秒）
   */
  setCacheExpireTime(expireTime) {
    this.cacheExpireTime = expireTime
  }

  /**
   * 获取缓存统计信息
   * @returns {Object} 缓存统计信息
   */
  getCacheStats() {
    return {
      size: this.urlCache.size,
      expireTime: this.cacheExpireTime
    }
  }
}

// 创建单例实例
const fileUtils = new FileUtils()

export default fileUtils

// 导出常用方法的快捷方式
export const getSecureFileUrl = (filePath, useCache = true) => fileUtils.getSecureUrl(filePath, useCache)
export const getSecureFileUrls = (filePaths, useCache = true) => fileUtils.getSecureUrls(filePaths, useCache)
export const clearFileUrlCache = (filePath) => fileUtils.clearCache(filePath)


// WEBPACK FOOTER //
// ./src/utils/fileUtils.js